import { useMutation } from "@tanstack/react-query"
import { Check, RotateCw, ScanText, X } from "lucide-react"
import { useEffect, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { ProgresoLectura } from "@/componentes/ordenes/ProgresoLectura"
import { monto as comoCifra } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { comoFecha } from "@/lib/fechas"

export type ComprobanteLeido = {
  es_comprobante_de_pago: boolean
  estado_operacion: string
  tipo_operacion: string
  monto: string | null
  monto_literal: string | null
  comision: string | null
  moneda: string | null
  fecha: string | null
  referencia: string | null
  banco_origen: string | null
  banco_destino: string | null
  cuenta_origen: string | null
  cuenta_destino: string | null
  telefono_destino: string | null
  destino_tipo: "CUENTA" | "TELEFONO" | "CORREO" | "BILLETERA" | "NO_INDICA"
  beneficiario: string | null
  beneficiario_id: string | null
  concepto: string | null
  confianza: "ALTA" | "MEDIA" | "BAJA"
  dudas: string[]
  modelo_usado: string
}

export type Sugerencia = { id: number; nombre: string; motivo: string; moneda: string | null }

export type Lectura = {
  comprobante: ComprobanteLeido
  se_puede_usar: boolean
  bloqueos: string[]
  avisos: string[]
  diferencia: string | null
  /** La cuenta de la agencia y la vía que señala el comprobante, si son únicas. */
  cuenta_sugerida: Sugerencia | null
  metodo_sugerido: Sugerencia | null
}

/**
 * Lee con IA el comprobante recién adjuntado y PROPONE sus datos al modal de pago.
 *
 * ⚠ **Se lee SÓLO si se pide** («Leer con IA»). Se leía solo al adjuntar, y eso
 * gastaba la cuota —20 lecturas al día por modelo en el nivel gratuito— con cada
 * comprobante, y no dejaba subir uno sin mandarlo a Google (2026-09-28).
 *
 * ⚠ **Nada se rellena sin confirmar.** Lo que el modelo
 * leyó se enseña al lado de lo que se va a asentar, y sólo «Usar estos datos» lo pasa
 * al formulario: un monto mal leído que entrara solo sería un débito falso con el
 * comprobante bueno al lado, que es lo más difícil de ver después.
 *
 * ⚠ **El monto sólo se propone si el comprobante es de la moneda de la cuenta.** Un
 * Zelle en dólares leído sobre una cuenta en bolívares no es lo debitado de ESA
 * cuenta: se avisa y se deja el monto como está.
 *
 * ⚠ **La cuenta y el método también se proponen**, y los decide el SERVIDOR contra el
 * catálogo: la cuenta por la terminación o el banco de origen —sólo si hay UNA—, y la
 * vía por el destino: un teléfono es pago móvil; un número de cuenta, transferencia del
 * mismo banco o de otro según el banco de destino.
 *
 * ⚠ **Sale a Google, y por eso sólo con `factura_ia:usar`.** Quien no lo tiene adjunta
 * el comprobante como siempre y teclea los datos.
 */
export function LecturaComprobante({
  archivo,
  ordenId,
  divisaCuenta,
  onUsar,
}: {
  archivo: File
  ordenId: number
  divisaCuenta: string | null
  onUsar: (lectura: Lectura, usarMonto: boolean) => void
}) {
  const [cerrada, setCerrada] = useState(false)
  const leer = useMutation({
    mutationFn: (a: File) => {
      const datos = new FormData()
      datos.append("archivo", a)
      datos.append("orden_id", String(ordenId))
      return api.post<Lectura>("/api/v1/facturas-ia/comprobante", datos)
    },
  })
  const { mutate, reset } = leer

  // Otro archivo —el bueno arrastrado encima del equivocado—: lo leído era del anterior.
  // biome-ignore lint/correctness/useExhaustiveDependencies: se reinicia AL CAMBIAR de archivo
  useEffect(() => {
    setCerrada(false)
    reset()
  }, [archivo, reset])

  if (cerrada) return null
  if (leer.isIdle) {
    return (
      <div className="flex flex-wrap items-center justify-end gap-2 text-xs">
        <span style={{ color: "var(--fg-muted)" }}>
          Opcional: la imagen se envía a Google (Gemini) para leerla.
        </span>
        <Boton tamano="sm" onClick={() => mutate(archivo)}>
          <ScanText size={14} /> Leer con IA
        </Boton>
      </div>
    )
  }
  if (leer.isPending) return <ProgresoLectura archivos={1} documento="comprobante" />
  if (leer.isError) {
    // ⚠ **Reintentar lee el MISMO archivo**, sin quitarlo y volver a adjuntarlo: la causa
    // típica —la cuota por minuto del nivel gratuito, Gemini saturado— se pasa sola.
    return (
      <div className="space-y-2 text-sm" role="alert">
        <p style={{ color: "var(--destructive)" }}>
          No se pudo leer el comprobante:{" "}
          {leer.error instanceof ErrorApi ? leer.error.message : "error inesperado"}. Reintenta o
          teclea los datos a mano.
        </p>
        <div className="flex justify-end gap-2">
          <Boton tamano="sm" onClick={() => setCerrada(true)}>
            <X size={14} /> Ignorar
          </Boton>
          <Boton tamano="sm" variante="primario" onClick={() => mutate(archivo)}>
            <RotateCw size={14} /> Reintentar
          </Boton>
        </div>
      </div>
    )
  }
  const r = leer.data
  if (!r?.comprobante) return null
  const c = r.comprobante
  // La cuenta que se va a usar: la propuesta, si la hay, o la ya elegida.
  const monedaCuenta = r.cuenta_sugerida?.moneda ?? divisaCuenta
  const monedaDistinta = c.moneda !== null && monedaCuenta !== null && c.moneda !== monedaCuenta
  const filas: [string, string | null][] = [
    ["Monto", c.monto ? `${comoCifra(c.monto)} ${c.moneda ?? ""}` : null],
    ["Comisión", c.comision ? comoCifra(c.comision) : null],
    ["Fecha", c.fecha ? comoFecha(c.fecha) : null],
    ["Referencia", c.referencia],
    ["Beneficiario", [c.beneficiario, c.beneficiario_id].filter(Boolean).join(" · ") || null],
    ["Bancos", [c.banco_origen, c.banco_destino].filter(Boolean).join(" → ") || null],
    ["Destino", c.telefono_destino ?? c.cuenta_destino],
    [
      "Cuenta que paga",
      r.cuenta_sugerida ? `${r.cuenta_sugerida.nombre} · ${r.cuenta_sugerida.motivo}` : null,
    ],
    [
      "Método",
      r.metodo_sugerido ? `${r.metodo_sugerido.nombre} · ${r.metodo_sugerido.motivo}` : null,
    ],
  ]

  return (
    <section className="tarjeta space-y-3 p-3 text-sm" aria-label="Datos leídos del comprobante">
      <h3 className="flex items-center gap-2 font-semibold">
        <ScanText size={16} aria-hidden={true} style={{ color: "var(--primary)" }} />
        Leído del comprobante
        <span className="text-xs font-normal" style={{ color: "var(--fg-muted)" }}>
          · confianza {c.confianza.toLowerCase()}
        </span>
      </h3>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
        {filas.map(([etiqueta, valor]) => (
          <div key={etiqueta} className="contents">
            <dt style={{ color: "var(--fg-muted)" }}>{etiqueta}</dt>
            <dd className="tabular-nums">{valor ?? "—"}</dd>
          </div>
        ))}
      </dl>
      {r.bloqueos.length > 0 && (
        <ul className="list-disc space-y-1 pl-5" style={{ color: "var(--destructive)" }}>
          {r.bloqueos.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      )}
      {(r.avisos.length > 0 || monedaDistinta || c.dudas.length > 0) && (
        <ul className="list-disc space-y-1 pl-5" style={{ color: "var(--warning)" }}>
          {monedaDistinta && (
            <li>
              El comprobante es en {c.moneda} y la cuenta en {monedaCuenta}: el monto no se propone.
            </li>
          )}
          {r.avisos.map((a) => (
            <li key={a}>{a}</li>
          ))}
          {c.dudas.map((d) => (
            <li key={d}>{d}</li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <Boton tamano="sm" onClick={() => setCerrada(true)}>
          <X size={14} /> Ignorar
        </Boton>
        {r.se_puede_usar && (
          <Boton
            tamano="sm"
            variante="primario"
            onClick={() => {
              onUsar(r, !monedaDistinta)
              setCerrada(true)
            }}
          >
            <Check size={14} /> Usar estos datos
          </Boton>
        )}
      </div>
    </section>
  )
}
